import { Box, Button, MenuItem, Stack, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AppButton from "../../components/app-button/AppButton";
import AppDialog from "../../components/app-dialog/AppDialog";
import {
    addUser,
    getUserByUid,
    getUserRoles,
    getUsersByStore,
    updateUser,
} from "../../reduxStore/slices/userSlice";
import { useNotify } from "../../utils/snackbarUtils";

function UserDialog({ isAddDialogOpen, setIsAddDialogOpen, uid }) {
  const { showSuccess, handleApiError, showError } = useNotify();
  const dispatch = useDispatch();

  const {
    roles,
    list,
    loading: isLoading,
  } = useSelector((state) => state.user);
  const { user } = useSelector((state) => state.auth);
  const storeId = user?.storeId;

  const [id, setId] = useState("");
  const [userMobileNo, setUserMobileNo] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [role, setRole] = useState(null);
  const [recordStatus, setRecordStatus] = useState("");

  const getRoles = async () => {
    try {
      await dispatch(getUserRoles({ storeId })).unwrap();
    } catch (err) {
      handleApiError(err);
    }
  };

  useEffect(() => {
    getRoles();
  }, []);

  const getUserDetails = async () => {
    try {
      const user = await dispatch(getUserByUid({ storeId, uid })).unwrap();
      if (user) {
        const role = user.roles && user.roles.length ? user.roles[0] : null;
        setId(user.id || "");
        setFirstName(user.first_name || "");
        setLastName(user.last_name || "");
        setUserMobileNo(user.mobile_no || "");
        setEmail(user.email || "");
        setCity(user.city || "");
        setRole(role);
        setRecordStatus(user.record_status || "");
      } else {
        showError("User not found");
        setIsAddDialogOpen(false);
      }
    } catch (err) {
      handleApiError(err);
    }
  };

  useEffect(() => {
    if (uid && list?.length) {
      getUserDetails();
    }
  }, [uid, list]);

  const handleSaveUser = async () => {
    try {
      const obj = {
        store_id: storeId,
        first_name: firstName,
        last_name: lastName,
        mobile_no: userMobileNo,
        email: email,
        city: city,
        roles: role?.role_uid ? [{ role_uid: role.role_uid }] : [],
      };
      const res = await dispatch(addUser(obj)).unwrap();
      await dispatch(getUsersByStore({ storeId }));
      setIsAddDialogOpen(false);
      showSuccess(res.message);
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleUpdateUser = async () => {
    try {
      const obj = {
        id: id,
        uid: Number(uid),
        store_id: storeId,
        first_name: firstName,
        last_name: lastName,
        mobile_no: userMobileNo,
        email: email,
        city: city,
        token_version: 1,
        record_status: recordStatus,
        roles: role ? [role] : [],
      };
      const res = await dispatch(updateUser(obj)).unwrap();
      await dispatch(getUsersByStore({ storeId }));
      setIsAddDialogOpen(false);
      showSuccess(res.message);
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleDialogCancel = () => {
    setIsAddDialogOpen(false);
  };

  const handleRoleChange = (event) => {
    const selectedRole = roles.find(
      (roleItem) => roleItem.role_uid === event.target.value,
    );
    if (selectedRole) {
      const roleWithFlag = {
        ...selectedRole,
        is_new_record: 1,
        record_status: "A",
      };
      setRole(roleWithFlag);
    } else {
      setRole(null);
    }
  };

  const dialogAddUserFooter = (
    <>
      <Box
        style={{
          display: "flex",
          gap: 15,
        }}
      >
        <Button size="medium" onClick={handleDialogCancel}>
          Cancel
        </Button>
        <AppButton
          disabled={isLoading}
          isLoading={isLoading}
          fullWidth
          size="medium"
          variant="contained"
          onClick={uid ? handleUpdateUser : handleSaveUser}
        >
          {uid ? "Update" : "Save"}
        </AppButton>
      </Box>
    </>
  );

  return (
    <AppDialog
      maxWidth="sm"
      isOpen={isAddDialogOpen}
      id={"add_user_dialog"}
      title={<>{uid ? "Edit User" : "Add User"}</>}
      footer={dialogAddUserFooter}
    >
      <Box
        style={{
          margin: "20px 5px",
        }}
      >
        <Stack
          spacing={3}
          style={{
            marginTop: 15,
            marginBottom: 15,
          }}
        >
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              autoComplete="off"
              autoFocus
              label="First Name *"
              variant="outlined"
              name="first_name"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
            />
            <TextField
              label="Last Name *"
              autoComplete="off"
              name="last_name"
              type="text"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="User Mobile No. *"
              placeholder=""
              autoComplete="off"
              name="user_mobile_no"
              type="number"
              value={userMobileNo}
              onChange={(event) => setUserMobileNo(event.target.value)}
            />
            <TextField
              autoComplete="off"
              label="Email ID (Optional)"
              variant="outlined"
              name="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              autoComplete="off"
              label="City (Optional)"
              variant="outlined"
              name="city"
              value={city}
              onChange={(event) => setCity(event.target.value)}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              select
              fullWidth
              label="Role"
              variant="outlined"
              value={role?.role_uid || ""}
              onChange={handleRoleChange}
            >
              <MenuItem value="">Select</MenuItem>
              {roles.map((roleItem) => (
                <MenuItem key={roleItem.role_uid} value={roleItem.role_uid}>
                  {roleItem.role_name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Stack>
      </Box>
    </AppDialog>
  );
}

export default UserDialog;
